import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { reportClientError } from '../utils/clientLogger';
import {
  getTransmissionLabel,
  getPaymentMethodLabel,
  getUrgencyLabel,
  getTradeinText,
  INITIAL_CAR_FORM_DATA
} from '../data/questionarioCarrosConfig';
import { QuestionarioCarrosSuccess } from '../components/questionarios/carros/QuestionarioCarrosSuccess';
import { QuestionarioCarrosProgress } from '../components/questionarios/carros/QuestionarioCarrosProgress';
import { QuestionarioCarrosStepOne } from '../components/questionarios/carros/QuestionarioCarrosStepOne';
import { QuestionarioCarrosStepTwo } from '../components/questionarios/carros/QuestionarioCarrosStepTwo';
import { QuestionarioCarrosStepThree } from '../components/questionarios/carros/QuestionarioCarrosStepThree';
import { QuestionarioCarrosStepFour } from '../components/questionarios/carros/QuestionarioCarrosStepFour';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;













const QuestionarioCarros = () => {
  const { codigo } = useParams();
  const [searchParams] = useSearchParams();
  const vendedorCodigo = codigo || searchParams.get('v') || localStorage.getItem('vendedor_codigo') || '';
  
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_CAR_FORM_DATA);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (vendedorCodigo) {
      localStorage.setItem('vendedor_codigo', vendedorCodigo);
    }
  }, [vendedorCodigo]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const nextStep = () => setStep(prev => Math.min(prev + 1, 4));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: `
=== QUESTIONÁRIO DE INTERESSE - CARROS ===

DADOS PESSOAIS:
- Nome: ${formData.name}
- Email: ${formData.email}
- Telefone: ${formData.phone}

TIPO DE VEÍCULO:
- Categoria: ${formData.carType}
- Marca preferida: ${formData.brand || 'Não especificada'}
- Modelos de interesse: ${formData.preferredModels || 'Não especificado'}

CARACTERÍSTICAS:
- Ano: ${formData.yearRange}
- Quilometragem máxima: ${formData.maxKm}
- Câmbio: ${getTransmissionLabel(formData.transmission)}
- Combustível: ${formData.fuel}

ORÇAMENTO:
- Valor disponível: ${formData.budget}
- Forma de pagamento: ${getPaymentMethodLabel(formData.paymentMethod)}

TROCA:
${getTradeinText(formData.hasTradein, formData.tradeinDetails)}

URGÊNCIA: ${getUrgencyLabel(formData.urgency)}

OBSERVAÇÕES:
${formData.observations || 'Nenhuma'}
        `
      });

      setSubmitted(true);
    } catch (error) {
      reportClientError('Error submitting questionnaire:', error);
      alert('Erro ao enviar questionário. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <QuestionarioCarrosSuccess />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100" data-testid="questionario-carros-page">
      {/* Header */}
      <div className="bg-white shadow-sm" data-testid="questionario-carros-header">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-gray-600 hover:text-gray-900" data-testid="questionario-carros-back-link">
            ← Voltar ao Site
          </Link>
          <h1 className="text-xl font-bold text-orange-600" data-testid="questionario-carros-brand">RH Consulting Holdings</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <QuestionarioCarrosProgress step={step} />

        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8" data-testid="questionario-carros-form-card">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-4xl">🚗</span>
            <div>
              <h2 className="text-2xl font-bold text-gray-900" data-testid="questionario-carros-title">Questionário de Interesse</h2>
              <p className="text-gray-600" data-testid="questionario-carros-subtitle">Encontre o carro ideal para você</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-testid="questionario-carros-form">
            {step === 1 && <QuestionarioCarrosStepOne formData={formData} handleChange={handleChange} />}
            {step === 2 && <QuestionarioCarrosStepTwo formData={formData} handleChange={handleChange} />}
            {step === 3 && <QuestionarioCarrosStepThree formData={formData} handleChange={handleChange} />}
            {step === 4 && <QuestionarioCarrosStepFour formData={formData} handleChange={handleChange} />}

            {/* Navigation Buttons */}
            <div className="flex gap-3 mt-8" data-testid="questionario-carros-navigation-buttons">
              {step > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  data-testid="questionario-carros-prev-button"
                >
                  ← Voltar
                </button>
              )}
              {step < 4 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={
                    (step === 1 && (!formData.name || !formData.email || !formData.phone)) ||
                    (step === 2 && !formData.carType)
                  }
                  className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                  data-testid="questionario-carros-next-button"
                >
                  Próximo →
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={submitting || !formData.urgency || !formData.paymentMethod}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                  data-testid="questionario-carros-submit-button"
                >
                  {submitting ? 'Enviando...' : '✓ Enviar Questionário'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuestionarioCarros;
